import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Divider,
  IconButton,
  useTheme,
  Tooltip,
  Link
} from '@mui/material';
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  RemoveCircle as SkippedIcon,
  Visibility as ViewIcon,
  LocalOffer as TagIcon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';
import { statusColors, statusMap } from '../../dashboard/components';
import useAwaitModal from '../../../hooks/useAwaitModal';
import { useParams } from 'react-router-dom';
import { TRACKING_IDS, trackMixPanel } from '../../../mixpanel.constants';
import CreateFailureTag from '../../TestRuns/components/CreateFailureTagModal';

const StatusIcon = ({ status }) => {
  const iconSize = 'small';
  switch (status) {
    case 'passed':
      return <CheckCircleIcon fontSize={iconSize} />;
    case 'failed':
      return <ErrorIcon fontSize={iconSize} />;
    case 'skipped':
      return <SkippedIcon fontSize={iconSize} />;
    default:
      return <WarningIcon fontSize={iconSize} />;
  }
};

export const processString = (inputString) => {
    if (!inputString) return null;
    return inputString.toLowerCase().replace(/,/g, '').split(' ').join('-');
  };

export const StatusBadge = ({
  status,
  error,
  errorStep,
  onViewDetails,
  index,
  failureTag,
  refetchPreviousRuns,
  sessionId
}) => {
  const { batchId, testId } = useParams();
  const theme = useTheme();
  const _status = status in statusMap ? statusMap[status] : status;

  const [
    requestFailureTagModal,
    {
      open: openFailureTagModal,
      onClose: onCloseFailureTagModal,
      onComplete: completeFailureTagModal,
      ...otherProps
    }
  ] = useAwaitModal();

  const getStatusColor = () => statusColors[_status] || theme.palette.error.main;

  const handleViewError = () => {
    onViewDetails();
    trackMixPanel(TRACKING_IDS.SEE_ERROR_CLICKED, {
      position: 'Error Banner'
    });
  };

  const getFailureTagLabel = () => {
    return failureTag?.failure_type === 'new ui change'
      ? 'New UI Change'
      : failureTag?.failure_type || 'Tag test failure';
  };


  const linkText = processString(error);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          p: 1.5,
          borderRadius: 1,
          backgroundColor: theme.palette.background.paper,
          borderLeft: `4px solid ${getStatusColor()}`,
          boxShadow: theme.shadows[1],
          width: '100%'
        }}>
        {/* Status Indicator */}
        <Chip
          icon={<StatusIcon status={_status} />}
          label={_status.toUpperCase()}
          size="small"
          sx={{
            backgroundColor: `${getStatusColor()}20`,
            color: getStatusColor(),
            fontWeight: 600,
            px: 1
          }}
        />

        {/* Error Message */}
        {_status === 'failed' && error && (
          <Typography variant="body2" sx={{ flex: 1 }}>
            Step {index + 1} failed: {error}
          </Typography>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={1} alignItems="center">
          {/* View Error Button */}
          <Tooltip title="View error details">
            <Chip
              icon={<ViewIcon fontSize="small" />}
              label="See error"
              size="small"
              onClick={handleViewError}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            />
          </Tooltip>

          {/* Failure Tag Button */}
          {testId && (sessionId || batchId) && (
            <>
              <Divider orientation="vertical" flexItem sx={{ height: 24 }} />
              <Tooltip title={getFailureTagLabel()}>
                <Chip
                  icon={<TagIcon fontSize="small" />}
                  label={getFailureTagLabel()}
                  size="small"
                  onClick={() => requestFailureTagModal({ testData: failureTag })}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover
                    }
                  }}
                />
              </Tooltip>
            </>
          )}

          {/* Get Help Button */}
          <Divider orientation="vertical" flexItem sx={{ height: 24 }} />
          <Tooltip title="Get help with this error">
            <Chip
              icon={<HelpIcon fontSize="small" />}
              label="Get help"
              size="small"
              component={Link}
              href={`https://docs.getscandium.com/why-did-my-test-fail#${linkText}`}
              target="_blank"
              onClick={() => {
                trackMixPanel(TRACKING_IDS.GET_HELP_CLICKED, {
                  position: 'Error Banner'
                });
              }}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            />
          </Tooltip>
        </Stack>
      </Stack>

      <CreateFailureTag
        open={openFailureTagModal}
        onClose={onCloseFailureTagModal}
        onComplete={completeFailureTagModal}
        refetchRuns={refetchPreviousRuns}
        sessionId={sessionId}
        {...otherProps}
      />
    </Box>
  );
};
